import React, { useState } from "react";
import { Box } from "@mui/material";
import Select, { TOption, TOptionType } from "../../../Components/Select";
import StateConditionPicker, {
  TStateOperator,
} from "../../../Components/StateConditionPicker";
import {
  getDeviceOptions,
  getStateAttributeOptions,
  getStateAttributeValueOptions,
} from "../utils";
import { IDevice } from "../../devices/types";
import { TStateCondition } from "./types";
import { useTranslation } from "react-i18next";

interface IStateConditionFormProps {
  condition: TStateCondition;
  devices: IDevice[];
  onChange: (stateCondition: TStateCondition) => void;
}

const StateConditionForm = (props: IStateConditionFormProps) => {
  const { condition, devices, onChange } = props;
  const device_id = condition.device_id || devices[0].id;
  const [deviceId, setDeviceId] = useState<number>(device_id);

  const [attributeOptions, setAttributeOptions] = useState<
    TOption<TOptionType>[]
  >(getStateAttributeOptions(devices, deviceId));

  const attribute = condition.attribute || attributeOptions[0].value;
  const [stateOptions, setStateOptions] = useState<TOption<TOptionType>[]>(
    getStateAttributeValueOptions(devices, deviceId, attribute as string)
  );

  const initialState = condition.state || (stateOptions[0].value as string);
  const [state, setState] = useState<string>(initialState);

  const [stateCondition, setStateCondition] = useState<TStateCondition>({
    device_id: device_id,
    attribute: attributeOptions[0].value as string,
    ...condition,
  });
  const { t } = useTranslation();

  const updateStateCondition = (newStateCondition: TStateCondition) => {
    setStateCondition(newStateCondition);
    onChange(newStateCondition);
  };

  return (
    <>
      <Select
        fullWidth
        label={t("Device")}
        options={getDeviceOptions(devices)}
        value={stateCondition.device_id}
        onChange={(event) => {
          const newDeviceId = parseInt(event.target.value);
          const newAttributeOptions = getStateAttributeOptions(
            devices,
            newDeviceId
          );
          setDeviceId(newDeviceId);
          setAttributeOptions(newAttributeOptions);

          const newStateAttributeOptions = getStateAttributeValueOptions(
            devices,
            newDeviceId,
            newAttributeOptions[0].value as string
          );
          setStateOptions(newStateAttributeOptions);
          setState(newStateAttributeOptions[0].value as string);

          updateStateCondition({
            ...stateCondition,
            device_id: newDeviceId,
            attribute: newAttributeOptions[0].value as string,
            state: newStateAttributeOptions[0].value as string,
          });
        }}
        margin="none"
        data-cy="state-condition-select-device"
      />
      <Box gap={1} display="flex" flexDirection="row">
        <Select
          label={t("Attribute")}
          value={stateCondition.attribute}
          options={attributeOptions}
          onChange={(event) => {
            const newAttribute = event.target.value;
            const newStateAttributeOptions = getStateAttributeValueOptions(
              devices,
              deviceId,
              newAttribute
            );
            setStateOptions(newStateAttributeOptions);
            setState(newStateAttributeOptions[0].value as string);

            updateStateCondition({
              ...stateCondition,
              attribute: newAttribute,
              state: newStateAttributeOptions[0].value as string,
            });
          }}
          margin="none"
          style={{ width: "50%" }}
          data-cy="state-condition-select-attribute"
        />
        <StateConditionPicker
          label={t("Condition")}
          operator={stateCondition.operator}
          state={state}
          stateOptions={stateOptions}
          onConditionChange={(operator: TStateOperator, state: string) => {
            setState(state);
            updateStateCondition({
              ...stateCondition,
              operator: operator,
              state: state,
            });
          }}
          style={{ width: "50%" }}
          data-cy="state-condition-condition-picker"
        />
      </Box>
    </>
  );
};

export default StateConditionForm;
